import type { Dayjs } from 'dayjs';

import type { DateRangePickerValue } from './date-range-picker';
import { getDateRangeFromValue } from './get-date-range-from-value';
import { getPresetDateRange, type PresetName,PRESETS } from './get-preset-date-range';
import type { DateRange } from './types';

export type DateRangePickerBounds = {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  allowedPresetNames?: readonly PresetName[];
};

export function isSameDateRangePickerValue(
  left: DateRangePickerValue,
  right: DateRangePickerValue,
) {
  if (left.type !== right.type) {
    return false;
  }

  if (left.type === 'preset' && right.type === 'preset') {
    return left.presetName === right.presetName;
  }

  if (left.type !== 'dateRange' || right.type !== 'dateRange') {
    return false;
  }

  return (
    left.dateRange.from.toJSON() === right.dateRange.from.toJSON() &&
    left.dateRange.to.toJSON() === right.dateRange.to.toJSON()
  );
}

export function clampDayjsToDateRangeBounds(
  value: Dayjs,
  { minDate, maxDate }: DateRangePickerBounds,
) {
  if (minDate && value.isBefore(minDate)) {
    return minDate;
  }

  if (maxDate && value.isAfter(maxDate)) {
    return maxDate;
  }

  return value;
}

export function clampDateRangeToBounds(
  value: DateRange,
  bounds: DateRangePickerBounds,
): DateRange {
  const from = clampDayjsToDateRangeBounds(value.from, bounds);
  const to = clampDayjsToDateRangeBounds(value.to, bounds);

  if (from.isAfter(to)) {
    return { from, to: from };
  }

  return { from, to };
}

export function getAllowedDateRangePickerPresetNames({
  minDate,
  maxDate,
}: Omit<DateRangePickerBounds, 'allowedPresetNames'>): PresetName[] {
  return PRESETS.filter(({ name }) => {
    const range = getPresetDateRange(name);

    return (
      (!minDate || !range.from.isBefore(minDate)) &&
      (!maxDate || !range.to.isAfter(maxDate))
    );
  }).map(({ name }) => name);
}

export function clampDateRangePickerValueToBounds(
  value: DateRangePickerValue,
  bounds: DateRangePickerBounds,
): DateRangePickerValue {
  const clampedDateRange = clampDateRangeToBounds(
    getDateRangeFromValue(value),
    bounds,
  );

  if (value.type === 'preset') {
    const allowedPresetNames =
      bounds.allowedPresetNames ??
      getAllowedDateRangePickerPresetNames(bounds);

    if (allowedPresetNames.includes(value.presetName)) {
      return value;
    }
  }

  const clampedValue: DateRangePickerValue = {
    type: 'dateRange',
    dateRange: clampedDateRange,
  };

  return isSameDateRangePickerValue(value, clampedValue)
    ? value
    : clampedValue;
}
